import {FC, useContext, useCallback, useState, useEffect} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {SocketContext} from "@/contexts/socketContext";
import {palette} from "@/constants";
import {FormControlLabel, Radio, RadioGroup, Stack, TextField} from "@mui/material";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {Dayjs} from "dayjs";
import {SOCKET_EVENTS, StartCourseEnrollmentsPayload, JobStatusCallback, ENROLLMENT_TYPES, EnrollmentType} from "@buildwitt/shapeshift-types";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import InfoIcon from "@mui/icons-material/Info";
import {CustomTooltip} from "@/components/common";

interface IProps {
	open: boolean;
	onClose: () => void;
	selectedUserIds: number[];
	courseId: number;
	onLoadingChange: (isLoading: boolean) => void;
}

const buttonSx = {
	border: "none",
	boxShadow: "none",
	color: "primary.main",
	"&:hover": {
		backgroundColor: palette.orange08
	}
};

export const BulkEnrollmentConfirmationModal: FC<IProps> = ({open, onClose, selectedUserIds, courseId, onLoadingChange}) => {
	const {t} = useSSTranslation();
	const socket = useContext(SocketContext);
	const {dueDateAssignments} = useDevCycleSdk();
	const [enrollmentType, setEnrollmentType] = useState<EnrollmentType>(ENROLLMENT_TYPES.PRIORITIZE_SCHEDULE);
	const [dueDate, setDueDate] = useState<Dayjs | null>(null);

	useEffect(() => {
		if (!open) {
			setEnrollmentType(ENROLLMENT_TYPES.PRIORITIZE_SCHEDULE);
			setDueDate(null);
		}
	}, [open]);

	useEffect(() => {
		if (!dueDateAssignments && enrollmentType === ENROLLMENT_TYPES.DUE_DATE_SCHEDULE) {
			setEnrollmentType(ENROLLMENT_TYPES.PRIORITIZE_SCHEDULE);
			setDueDate(null);
		}
	}, [dueDateAssignments, enrollmentType]);

	const handleConfirm = useCallback(() => {
		if (!open || !socket) {
			return;
		}

		if (enrollmentType === ENROLLMENT_TYPES.DUE_DATE_SCHEDULE && !dueDate) {
			return;
		}

		const payload: StartCourseEnrollmentsPayload = {
			userIds: selectedUserIds,
			courseId,
			enrollmentType
		};

		if (enrollmentType === ENROLLMENT_TYPES.DUE_DATE_SCHEDULE && dueDate) {
			payload.dueDate = dueDate.toISOString();
		}

		onLoadingChange(true);

		socket.emit(SOCKET_EVENTS.START_COURSE_ENROLLMENTS, payload, (response: JobStatusCallback) => {
			window.dispatchEvent(new CustomEvent("courseEnrollmentsCompleted", {detail: response}));
		});

		onClose();
	}, [open, socket, selectedUserIds, courseId, enrollmentType, dueDate, onLoadingChange, onClose]);

	const isConfirmDisabled = enrollmentType === ENROLLMENT_TYPES.DUE_DATE_SCHEDULE && !dueDate;

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				if (reason !== "backdropClick") {
					onClose();
				}
			}}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			PaperProps={{
				sx: {
					bgcolor: "background.paperElevation3",
					borderColor: "background.paperElevation3"
				}
			}}
		>
			<DialogTitle id="alert-dialog-title">
				{t("bulk_enrollment_modal_title", "Enroll Users in Course", {count: selectedUserIds.length})}
			</DialogTitle>
			<DialogContent>
				<Stack spacing={2}>
					<DialogContentText id="alert-dialog-description" variant="body1" color="text.primary">
						{t("bulk_enrollment_modal_body_copy", "This will enroll the selected users in the course. Choose how you want to schedule this course in their training feed.", {count: selectedUserIds.length})}
					</DialogContentText>
					<RadioGroup
						row
						value={enrollmentType}
						onChange={(e) => setEnrollmentType(e.target.value as EnrollmentType)}
						aria-labelledby="enrollment-type-group-label"
					>
						<FormControlLabel
							value={ENROLLMENT_TYPES.PRIORITIZE_SCHEDULE}
							control={<Radio />}
							label={
								<Stack direction="row" gap={1}>
									{t("bulk_enrollment_prioritize_option", "Prioritize in Training Feed")}
									<CustomTooltip title={t("prioritized_enrollment_tooltip_copy", "Prioritized enrollment will start on users next available day")}>
										<InfoIcon color="disabled" />
									</CustomTooltip>
								</Stack>
							}
						/>
						{dueDateAssignments && (
							<FormControlLabel
								value={ENROLLMENT_TYPES.DUE_DATE_SCHEDULE}
								control={<Radio />}
								label={t("bulk_enrollment_due_date_option", "Due Date")}
							/>
						)}
					</RadioGroup>
					{enrollmentType === ENROLLMENT_TYPES.DUE_DATE_SCHEDULE && (
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								label={t("bulk_enrollment_due_date_label", "Due Date")}
								value={dueDate}
								renderInput={(params) => <TextField {...params} />}
								onChange={(newValue) => newValue && setDueDate(newValue)}
							/>
						</LocalizationProvider>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={onClose} sx={buttonSx}>
					{t("team_progress_calendar_cancel", "Cancel")}
				</Button>
				<Button
					variant="text"
					onClick={handleConfirm}
					sx={buttonSx}
					disabled={isConfirmDisabled}
				>
					{t("common_confirm", "Confirm")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
